import IdentityDocumentTypeAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/masters/identity-document-type/identity-document-type.entity';
import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, TemplateRef, ViewChild } from '@angular/core';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginator, MatPaginatorIntl, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavContainer } from '@angular/material/sidenav';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableModule, MatRowDef } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { IdentityDocumentTypeService } from './identity-document-type.service';
import { ICreateIdentityDocumentType } from './interfaces/create-identity-document-type.interface';
import { IUpdateIdentityDocumentType } from './interfaces/update-identity-document-type.interface';
import { CustomMatPaginatorIntl } from 'app/mock-api/common/customPaginatorIntl';
import { Subscription } from 'rxjs';
import { SearchService } from 'app/layout/common/search-page/search-page.service';
import { StatusFiltersComponent } from '../../filters/status-filters/status-filters.component';
import { HoverMenuDirective } from "@fuse/directives/hover-menu/hover-menu.directive";

@Component({
  selector: 'app-identity-document-type',
  standalone: true,
  templateUrl: './identity-document-type.component.html',
  styleUrl: './identity-document-type.component.scss',
  imports: [
    CommonModule,
    MatTableModule,
    MatDialogModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSortModule,
    MatPaginatorModule,
    MatIcon,
    MatSlideToggle,
    MatSelectModule,
    MatOptionModule,
    MatTooltipModule,
    MatExpansionModule,
    MatMenuModule,
    MatButtonModule,
    MatIconModule,
    MatRowDef,
    MatSidenavContainer,
    FormsModule,
    StatusFiltersComponent,
    HoverMenuDirective
  ],
  providers: [
    { provide: MatPaginatorIntl, useClass: CustomMatPaginatorIntl },
  ],
})
export class IdentityDocumentTypeComponent implements AfterViewInit, OnDestroy {
  displayedColumns: string[] = ["name", "statusId", "actions"];
  currentCreateIdentityDocumentType: ICreateIdentityDocumentType = this.initializeCreateIdentityDocumentType();
  currentUpdateIdentityDocumentType: IdentityDocumentTypeAttributes | undefined;

  dataSource: IdentityDocumentTypeAttributes[] = [];
  searchQuery = '';
  totalItems = 0;
  pageSize = 50;
  pageIndex = 0;

  dialogRefAdd: MatDialogRef<any>;
  dialogRefEdit: MatDialogRef<any>;

  @ViewChild("editModal") editModal!: TemplateRef<any>;
  @ViewChild("addModal") addModal!: TemplateRef<any>;
  @ViewChild("deleteDialog") deleteDialog!: TemplateRef<any>;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild("searchInput") searchInput!: ElementRef;

  //BUSCADOR
  searchSubscription: Subscription;

  // Status Filter Properties
  selectedStatuses: number[] = [1, 0]; // Default to show both active and inactive

  constructor(
    private readonly identityDocumentTypeService: IdentityDocumentTypeService,
    private readonly searchService: SearchService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
  ) {}

  ngAfterViewInit(): void {
    this.onHandleSearch();
    this.setupPaginationAndSorting();
    this.findAllWithCount();
  }

  private initializeCreateIdentityDocumentType(): ICreateIdentityDocumentType {
    return { code: '', name: '', externalKeys: { code: '' } };
  }

  private setupPaginationAndSorting(): void {
    this.paginator.page.subscribe(() => this.findAllWithCount());
    this.sort.sortChange.subscribe(() => {
      this.paginator.pageIndex = 0;
      this.findAllWithCount();
    });
  }

  onPageChange(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this.findAllWithCount();
  }

  findAllWithCount(): void {
    const pageIndex = this.paginator.pageIndex + 1;
    const sortField = this.getSortField();
    const sortDirection: "ASC" | "DESC" = this.sort.direction === "asc" ? "ASC" : "DESC";

    this.identityDocumentTypeService
      .findAllWithCount({
        currentPage: pageIndex,
        limitPerPage: this.pageSize,
        search: this.searchQuery,
        sort: sortField,
        sortDirection: sortDirection,
        status: this.selectedStatuses.length ? this.selectedStatuses : null,
      })
      .subscribe((data) => {
        this.dataSource = data?.data?.[0]?.data ?? [];
        this.totalItems = data?.data?.[0]?.pageInfo?.[0]?.total ?? 0;
        this.paginator.length = this.totalItems;
      });
  }

  private getSortField(): keyof IdentityDocumentTypeAttributes {
    const validSortFields: (keyof IdentityDocumentTypeAttributes)[] = ["code", "name", "statusId"];
    return validSortFields.includes(this.sort.active as keyof IdentityDocumentTypeAttributes) ? (this.sort.active as keyof IdentityDocumentTypeAttributes) : "name";
  }

  onSearchChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchQuery = input.value.trim();
    if (this.searchQuery.length >= 2 || this.searchQuery.length === 0) {
      this.paginator.pageIndex = 0;
      this.findAllWithCount();
    }
  }

  public onHandleSearch() {
    this.searchSubscription = this.searchService.searchTerm$
      .subscribe(term => {
        this.searchQuery = term;
        if (this.searchQuery.length >= 2 || this.searchQuery.length === 0) {
          this.paginator.pageIndex = 0;
          this.findAllWithCount();
        }
      });
  }

  trackByFn(index: number, item: IdentityDocumentTypeAttributes): string {
    return item._id.toString();
  }

  openCreateModal(): void {
    this.currentCreateIdentityDocumentType = this.initializeCreateIdentityDocumentType();
    this.dialogRefAdd = this.dialog.open(this.addModal, { panelClass: "identity-document-type-form-modal", disableClose: true })
    this.dialogRefAdd.afterClosed().subscribe((result) => { result });
  }

  openEditModal(identityDocumentType: IdentityDocumentTypeAttributes): void {
    this.currentUpdateIdentityDocumentType = {
      ...identityDocumentType,
      externalKeys: { code: identityDocumentType.externalKeys?.code ?? '' },
    };
    this.dialogRefEdit = this.dialog.open(this.editModal, { panelClass: "identity-document-type-form-modal", disableClose: true })
    this.dialogRefEdit.afterClosed().subscribe((result) => { result });
  }

  createIdentityDocumentType(): void {
    this.identityDocumentTypeService.create(this.currentCreateIdentityDocumentType).subscribe({
      next: () => {
        this.snackBar.open('Registro creado con éxito', 'Cerrar', { duration: 3000 });
        this.findAllWithCount();
        this.dialogRefAdd.close();
      },
      error: (err) => {
        this.snackBar.open(err.error.message || 'Error al crear el registro', 'Cerrar', { duration: 3000 });
      }
    });
  }

  updateIdentityDocumentType(id: string): void {
    if (this.currentUpdateIdentityDocumentType) {
      const updatedIdentityDocumentType: IUpdateIdentityDocumentType = { ...this.currentUpdateIdentityDocumentType };

      this.identityDocumentTypeService.update(id, updatedIdentityDocumentType).subscribe({
        next: () => {
          this.snackBar.open('Registro actualizado con éxito', 'Cerrar', { duration: 3000 });
          this.findAllWithCount();
          this.dialogRefEdit.close();
        },
        error: (err) => {
          this.snackBar.open(err.error.message || 'Error al actualizar el registro', 'Cerrar', { duration: 3000 });
        }
      });
    }
  }

  onStatusChange(event: boolean, identityDocumentType: IdentityDocumentTypeAttributes): void {
    const originalStatusId = identityDocumentType.statusId;
    const updatedStatusId = event ? 1 : 0;
    const updatedIdentityDocumentType = { ...identityDocumentType, statusId: updatedStatusId };

    this.identityDocumentTypeService.update(identityDocumentType._id.toString(), updatedIdentityDocumentType).subscribe({
      next: () => {
        this.findAllWithCount();
      },
      error: () => {
        identityDocumentType.statusId = originalStatusId; // Volver al estado original si falla
        this.snackBar.open('Error al actualizar el estado.', 'Cerrar', { duration: 3000 });
        this.findAllWithCount();
      }
    });
  }

  openDeleteDialog(identityDocumentType: IdentityDocumentTypeAttributes): void {
    this.dialog
      .open(this.deleteDialog, { data: identityDocumentType, panelClass: "delete-modal", disableClose: true })
      .afterClosed()
      .subscribe((result) => result && this.delete(identityDocumentType._id.toString()));
  }

  delete(id: string): void {
    this.identityDocumentTypeService.remove(id).subscribe(() => {
      this.findAllWithCount();
      this.snackBar.open('Registro eliminado con éxito', 'Cerrar', { duration: 3000 });
    });
  }

  // Status Filter Methods
  applyStatusFilter(): void {
    this.paginator.pageIndex = 0;
    this.findAllWithCount();
  }

  ngOnDestroy(): void {
    this.searchSubscription.unsubscribe();
  }
}
